export enum ChannelState {
  Connected = 'CONNECTED',
  Disconnected = 'DISCONNECTED',
}

export interface Persona {
  id: number
  name: string
  description?: string
  pilotPromptId: number
  avatarUrl?: string
  createdAt: string
  updatedAt: string
}

export interface Completion {
  assistantResponse?: string
  createdAt: number
  id: number
  persona?: Persona
  pilotFiles?: File[]
  pilotFunctionCalls?: PilotFunctionCall[]
  rating: number
  state: CompletionState
  threadKey?: string
  updating?: boolean
  userRequest: string
}

export interface CompletionsChannelPayload {
  data: Completion | CompletionDelta
  type: CompletionsChannelPayloadType
}

export enum CompletionsChannelPayloadType {
  Delta = 'delta',
  Object = 'object',
}

export interface CompletionDelta {
  id: number
  assistantResponseDelta: string
}

// Action Parameter Types
export interface CreateCompletionParams {
  agentKey: string
  fileIds?: Array<number>[]
  threadKey: string
  pilotThreadId: number
  context: object
  userRequest: string
  personaId?: number
}

export interface CompletionData {
  agentKey: string
  pilotFileIds?: Array<number>[]
  context: object
  userRequest: string
  threadKey: string
  pilotThreadId: number
  personaId?: number
}

export enum CompletionRole {
  Assistant = 'assistant',
  User = 'user',
}

export enum CompletionState {
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export interface File {
  id?: number
  clientId: string
  contentType: string
  name: string
  showPath?: string
  state: FileState | FileUploadState
  uploadError?: string
  progress?: number
}

export enum FileState {
  Processing = 'processing',
  Successful = 'successful',
  Failed = 'failed',
}

export enum FileUploadState {
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  UploadFailed = 'upload_failed',
}

export interface PilotContextType {
  agentKey: string
  authenticated: boolean
  context: object
  threadKey: string
}

// Redux State Types

export interface PilotThread {
  id: number
  userKey: string
  title: string
  createdAt: string
  updatedAt: string
}

export interface CompletionsState {
  channelState: ChannelState
  creating: boolean
  fetching: boolean
  hasNextPage: boolean
  activeFunctionCalls: Record<number, PilotFunctionCall>
  functionCallProgress: Record<number, string>
  currentThread?: PilotThread
  selectedPersona?: Persona
  personas: Persona[]
  personasLoading: boolean
  personasError?: string
}

export interface PersonaState {
  selectedPersona?: Persona
  personas: Persona[]
  loading: boolean
  error?: string
}

// Function Call Types

export enum FunctionCallState {
  Pending = 'pending',
  Executing = 'executing',
  Completed = 'completed',
  Failed = 'failed',
}

export interface PilotFunctionCall {
  id: number
  pilotCompletionId: number
  functionName: string
  parameters: Record<string, any>
  result?: Record<string, any>
  state: FunctionCallState
  errorMessage?: string
  previousState?: Record<string, any>
  createdAt: string
  updatedAt: string
}

export interface FunctionCallMessage {
  type: 'function_call'
  functionCall: PilotFunctionCall
  message?: string
}

export interface FunctionCallProgressMessage {
  type: 'function_call_progress'
  functionCallId: number
  state: FunctionCallState
  message: string
}

export type AssistantMessage =
  | { type: 'text'; content: string }
  | { type: 'function_call'; functionCall: PilotFunctionCall }
  | { type: 'thinking'; message: string }

export interface FunctionCallPayload {
  functionName: string
  parameters: Record<string, any>
}

export interface FunctionCallResult {
  success: boolean
  result?: any
  error?: string
}

// Channel payload types for function calls
export interface FunctionCallChannelPayload {
  data: PilotFunctionCall | FunctionCallProgressMessage
  type: FunctionCallChannelPayloadType
}

export enum FunctionCallChannelPayloadType {
  FunctionCall = 'function_call',
  Progress = 'function_call_progress',
}

// Authentication-related types for function calls
export interface FunctionCallAuthError {
  type: 'auth_required'
  message: string
  signInUrl: string
  signUpUrl: string
}
